import Link from "@/components/link";
import Image from "@/components/image";
import { ArrowLeft, Instagram } from "lucide-react";
import PlantAnimation from "@/components/plant-animation";

export default function Tequila() {
  return (
    <main className="min-h-screen bg-[#e6f0e0] flex flex-col">
      <div className="container mx-auto py-8 px-4 md:px-6 flex-grow">
        <Link href="/" className="inline-flex items-center text-gray-700 hover:text-gray-900 mb-8">
          <ArrowLeft size={16} className="mr-2" />
          Back to home
        </Link>

        <div className="max-w-4xl mx-auto">
          <div className="w-full max-w-2xl mx-auto mb-4 overflow-hidden rounded-lg">
            <PlantAnimation />
          </div>

          <div className="flex flex-col items-center justify-center mb-12">
            <h3 className="text-6xl font-bold mb-4">Kioete</h3>
            <Link
              href="https://www.instagram.com/kioete/"
              target="_blank"
              className="flex items-center text-gray-700 hover:text-gray-900"
            >
              <Instagram size={18} className="mr-2" />
              <span>Follow us on Instagram!</span>
            </Link>
          </div>

          <article className="prose prose-lg max-w-none">
            <h1 className="text-4xl font-bold mb-6">Tequila high fashion</h1>

            <p className="mb-4">
              One day, while driving back from a charming pueblo in Hidalgo, I came across a tequila factory. As was
              usual for that time of year, they were throwing away the remnants of the agave plants after the
              production. Being naturally curious, I found myself wondering, why waste so much? Isn't there a way to do
              something about it? Someone's trash is another man's treasure, right? Perhaps I stumbled upon mine.
            </p>

            <h2 className="text-3xl font-bold mt-10 mb-4">The Process</h2>

            <p className="mb-4">
              The tequila-making process can be summarized as follows: an agave plant is cultivated and allowed to grow
              for at least five years, after which it's harvested, baked, mashed into a liquid, and then distilled to
              produce the intoxicating beverage.
            </p>

            <p className="mb-4">
              Going deeper into the process, it's not the entire plant that's utilized, but rather its core. This core
              resembles a pineapple and contains the essential sugars responsible for that delicious tequila taste. It
              is this component that's placed in a furnace, mashed, and then processed to extract its juice.
            </p>

            <p className="mb-4">
              Now let's see this process from the perspective of residues. When cutting the core, half the weight of the
              plant is thrown away in the form of leaves. Then, after having cooked and smashed the core, this itself is
              thrown away to rot or inicinarate.
            </p>

            <p className="mb-4">
              The entire process of spawning tequila into existence implies the creation of ~70% the weight of the plant
              being trash.
            </p>

            <p className="mb-4">
              Now, this is badly enough and inefficient but it goes further. The trash is then either incinerated, which
              pollutes a lot, or thrown into a hole to rot, which pollutes a lot too (while decomposing).
            </p>

            <p className="mb-4">This is when my quest for a full circle tequila started.</p>

            <h2 className="text-3xl font-bold mt-10 mb-4">The Idea</h2>

            <div className="flex justify-center my-8">
              <Image
                src="/images/tequila-clothes.jpg"
                alt="Kioete Tequila Clothes"
                width={400}
                height={400}
                className="mx-auto rounded-lg"
              />
            </div>

            <p className="text-center font-medium text-xl my-6">
              Turn tequila industry trash into incredible, sustainable, organic fibers and make a high fashion clothing
              line.
            </p>

            <p className="mb-4">
              Financially speaking, tequila textiles themselves wouldn't produce enough money to scale up the
              production. Since the raw materials (agave residuals) are strictly finite (upper bound on tequila
              production), it would be extremely hard to get huge amounts of fabric. Plus, the incentive to sell the
              fabric is not correctly placed; big fashion companies will try to get stable production for the lowest
              price possible.
            </p>

            <p className="mb-4">
              Everything previously mentioned, however, rather than being a problem is an opportunity. Agave made
              fabrics are intrinsically unique and scarce, the production of limited amounts of fabric presents a
              special marketing opportunity. With a growing sustainable fabrics market, the introduction of self-made,
              high fashion, tequila clothing reveals itself.
            </p>

            <h2 className="text-3xl font-bold mt-10 mb-4">The Execution</h2>

            <div className="flex justify-center my-8">
              <Image
                src="/images/kiote-lab.png"
                alt="Kioete Lab Results"
                width={500}
                height={300}
                className="mx-auto rounded-lg"
              />
            </div>

            <p className="mb-4">
              The process of turning agave residues into textile fibers is not a trivial one. As a trained physicist and
              computer science nerd, some of these steps (chemical treatments) laid outside my particular circle of
              knowledge so I sought people who could do it.
            </p>

            <p className="mb-4">
              With the code name Kioete (after the Agave plant's blossoming flower), the project is now on its way. We
              have a team developing chemical treatments to turn the fibers into useful textiles and another team
              developing the machinery to scale fiber extraction.
            </p>

            <p className="mb-4">
              I have also sought partnerships with people in the industry. As of today, I have established a
              relationship with one of Monterrey's drink manufacturers, who uses Agave to make a special, extremely
              interesting, groundbreaking drink with cancer-fighting properties. He provides the necessary residues to
              make our tests. Also, I have talked with people in the Tequila industry, asking about their process and
              possible willingness to take their trash out for them.
            </p>

            <div className="flex justify-center my-8">
              <Image
                src="/images/tequila_man.jpeg"
                alt="Carrying a bag of agave leaves across campus"
                width={400}
                height={533}
                className="mx-auto rounded-lg"
              />
            </div>

            <p className="mb-4">
              The prospect of a full circle production of tequila, where the remnants are themselves used is as
              attractive as the drink itself.
            </p>
          </article>
        </div>
      </div>
    </main>
  );
}
